<template>
  <div class="login-page">
    <div class="login-container">
      <!-- Logo & Branding -->
      <div class="brand-section">
        <div class="logo-wrapper">
          <img src="@/assets/logo.svg" alt="לוגו הדרך אל הלב" class="logo" />
        </div>
        <h1 class="app-name">הדרך אל הלב</h1>
        <p class="app-tagline">התחברות למערכת</p>
      </div>

      <!-- Login Card -->
      <div class="login-card">
        <!-- Tab Switcher -->
        <div class="tab-switcher">
          <button
            type="button"
            :class="['tab-btn', { active: loginType === 'email' }]"
            @click="loginType = 'email'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>אימייל</span>
          </button>
        </div>

        <!-- Email Login Form -->
        <form @submit.prevent="onLoginSubmit" v-show="loginType === 'email'" class="login-form">
          <div class="input-group">
            <label for="email">אימייל</label>
            <div class="input-wrapper" :class="{ error: hasEmailError || hasLoginError }">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              <input
                id="email"
                type="email"
                placeholder="example@email.com"
                v-model="email"
                autocomplete="email"
              />
            </div>
            <span v-if="hasEmailError" class="error-text">עליך להזין אימייל תקין</span>
          </div>

          <div class="input-group">
            <label for="password">סיסמה</label>
            <div class="input-wrapper" :class="{ error: hasLoginError }">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <input
                id="password"
                :type="showPwd ? 'text' : 'password'"
                placeholder="הזן סיסמה"
                v-model="pwd"
                autocomplete="current-password"
              />
              <button type="button" class="toggle-pwd" @click="showPwd = !showPwd">
                <svg v-if="!showPwd" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
            <span v-if="hasLoginError" class="error-text">{{ loginErrMsg }}</span>
          </div>

          <button type="button" class="forgot-password" @click="handleForgotPassword">
            שכחת סיסמה?
          </button>

          <button type="submit" class="submit-btn" :disabled="isPending">
            <span v-if="!isPending">כניסה</span>
            <span v-else class="loading">
              <svg class="spinner" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-dasharray="31.4 31.4" />
              </svg>
              מתחבר...
            </span>
          </button>

          <div class="divider">
            <span>או</span>
          </div>

          <button type="button" class="create-account-btn" @click="handleCreateAccount">
            יצירת חשבון חדש
          </button>
        </form>

        <!-- Phone Login Form -->
        <form @submit.prevent="onSignInSubmit" v-show="loginType === 'phone'" class="login-form">
          <div class="input-group">
            <label for="phone">מספר טלפון</label>
            <div class="input-wrapper" :class="{ error: hasPhoneNumberError }">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <input
                id="phone"
                type="tel"
                placeholder="05X-XXXXXXX"
                v-model="phoneNumber"
                autocomplete="tel"
                dir="ltr"
                class="phone-input"
              />
              <span class="country-code">+972</span>
            </div>
            <span v-if="hasPhoneNumberError" class="error-text">{{ errMessage }}</span>
          </div>

          <div id="recaptcha-container"></div>

          <button type="submit" id="sign-in-button" class="submit-btn" :disabled="isPending">
            <span v-if="!isPending">שליחת קוד אימות</span>
            <span v-else class="loading">
              <svg class="spinner" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-dasharray="31.4 31.4" />
              </svg>
              שולח...
            </span>
          </button>

          <div class="divider">
            <span>או</span>
          </div>

          <button type="button" class="create-account-btn" @click="handleCreateAccount">
            יצירת חשבון חדש
          </button>
        </form>
      </div>

      <!-- Footer -->
      <p class="footer-text">בהתחברות אתה מסכים לתנאי השימוש</p>
    </div>
  </div>
</template>

<script setup>
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { onMounted, ref } from "vue"
import { useRouter } from "vue-router"
import { states } from '../../main_store'
import { getFunctions, httpsCallable } from "firebase/functions";
import { isValidEmail, isValidIsraeliPhoneNumber } from "@/scripts/utils";
import { showModal } from '@/main_store/modal';

const functions = getFunctions();
const updateLogin = httpsCallable(functions, 'update_login');

const showPwd = ref(false)

const auth = getAuth();
const router = useRouter()
const phoneNumber = ref('')
const hasPhoneNumberError = ref(false)
const isPending = ref(false)
const errMessage = ref('')

const email = ref('')
const pwd = ref('')
const hasEmailError = ref(false)
const hasLoginError = ref(false)
const loginErrMsg = ref('')

const loginType = ref('email')

let appVerifier

const handleCreateAccount = async () => {
  states.value.createAct = true
  router.push({ name: "Signup" });
}

const onSignInSubmit = async () => {
  try {
    if (isValidIsraeliPhoneNumber(phoneNumber.value)) {
      hasPhoneNumberError.value = false
      isPending.value = true
      const full_phone_number = `+972${phoneNumber.value.slice(1)}`

      const confirmationResult = await signInWithPhoneNumber(auth, full_phone_number.trim(), appVerifier);
      states.value.confirmationResult = confirmationResult

      router.push({ name: "Verification" })
    } else {
      hasPhoneNumberError.value = true
      errMessage.value = 'עליך להזין מספר טלפון תקין'
      throw new Error('עליך להזין מספר טלפון תקין')
    }
  } catch (error) {
    hasPhoneNumberError.value = true
    console.error("this is the error:", error)
    errMessage.value = 'ארעה שגיאה במהלך אישור הטלפון'
    throw new Error('ארעה שגיאה במהלך אישור הטלפון')
  } finally {
    isPending.value = false
  }
}


const onLoginSubmit = async () => {
  if (!isValidEmail(email.value.trim()) || !pwd.value) {
    hasLoginError.value = true
    loginErrMsg.value = 'יש להזין אימייל תקין וסיסמה!'
    return
  }
  try {
    isPending.value = true
    hasEmailError.value = false
    hasLoginError.value = false
    loginErrMsg.value = ''
    await signInWithEmailAndPassword(auth, email.value.trim(), pwd.value)
    router.push({ name: 'Home' })
  } catch (err) {
    console.error('❌ Email login error:', err)
    hasLoginError.value = true
    loginErrMsg.value = 'אימייל או סיסמה שגויים'
  } finally { isPending.value = false }
}


const handleForgotPassword = async () => {
  if (!isValidEmail(email.value.trim())) {
    hasEmailError.value = true
    return
  }
  try {
    isPending.value = true
    await sendPasswordResetEmail(auth, email.value.trim())
    showModal('קישור איפוס נשלח לאימייל', 'success')
  } catch (e) {
    showModal('שגיאה בשליחת קישור', 'error')
  }
  finally { isPending.value = false }
}

onMounted(() => {
  let el = document.getElementById('recaptcha-container');
  if (el) {
    appVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      'size': 'invisible',
    });
  }
})
</script>


<style scoped>
.login-page {
  min-height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
}

.login-container {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

/* Brand Section */
.brand-section {
  text-align: center;
  color: white;
}

.logo-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.logo {
  width: 50px;
  height: 50px;
}

.app-name {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 4px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.app-tagline {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
}

/* Login Card */
.login-card {
  width: 100%;
  background: white;
  border-radius: 20px;
  padding: 24px 22px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  direction: rtl;
}

/* Tab Switcher */
.tab-switcher {
  display: flex;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 20px;
  gap: 4px;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: #374151;
}

.tab-btn.active {
  background: white;
  color: #667eea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tab-btn svg {
  flex-shrink: 0;
}

/* Form Styles */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-group label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 46px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.input-wrapper:focus-within {
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.input-wrapper.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.input-wrapper.error:focus-within {
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.input-wrapper svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.input-wrapper:focus-within svg {
  color: #667eea;
}

.input-wrapper.error svg {
  color: #ef4444;
}

.input-wrapper input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  color: #1f2937;
  outline: none;
  width: 100%;
}

.input-wrapper input::placeholder {
  color: #9ca3af;
}

.phone-input {
  text-align: left;
}

.country-code {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  padding-right: 12px;
  border-right: 1px solid #e5e7eb;
}

.toggle-pwd {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  transition: color 0.2s;
}

.toggle-pwd:hover {
  color: #667eea;
}

.error-text {
  font-size: 13px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Forgot Password */
.forgot-password {
  align-self: flex-start;
  background: none;
  border: none;
  color: #667eea;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}

.forgot-password:hover {
  color: #5a67d8;
  text-decoration: underline;
}

/* Submit Button */
.submit-btn {
  width: 100%;
  height: 46px;
  padding: 0 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.35);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.45);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #9ca3af;
  font-size: 14px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

/* Create Account Button */
.create-account-btn {
  width: 100%;
  height: 44px;
  padding: 0 24px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-account-btn:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f8f7ff;
}

/* Footer */
.footer-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin: 0;
}

/* Mobile Responsive */
@media (max-width: 480px) {
  .login-page {
    padding: 16px;
    align-items: flex-start;
    padding-top: 30px;
  }

  .login-card {
    padding: 20px 18px;
    border-radius: 16px;
  }

  .logo-wrapper {
    width: 64px;
    height: 64px;
  }

  .logo {
    width: 40px;
    height: 40px;
  }

  .app-name {
    font-size: 22px;
  }

  .tab-btn {
    padding: 8px 10px;
    font-size: 13px;
  }

  .input-wrapper {
    height: 44px;
    padding: 0 12px;
  }

  .submit-btn {
    height: 44px;
    font-size: 14px;
  }

  .create-account-btn {
    height: 42px;
    font-size: 13px;
  }
}

/* Landscape mode */
@media (max-height: 600px) {
  .login-page {
    align-items: flex-start;
    padding-top: 20px;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .logo-wrapper {
    width: 50px;
    height: 50px;
    margin: 0;
  }

  .logo {
    width: 30px;
    height: 30px;
  }

  .app-name {
    font-size: 20px;
    margin: 0;
  }

  .app-tagline {
    display: none;
  }

  .login-card {
    padding: 20px;
  }

  .tab-switcher {
    margin-bottom: 16px;
  }

  .login-form {
    gap: 12px;
  }
}
</style>
